/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { isSanGabriel } from '../constants';

const commercialAcadiaTourIds = ['10086916', '10086917', '10086918', '10086919'];

const commercialMountEvansTourIds = ['10087443'];
const commercialRedRockCanyonTourIds = ['10075183'];

export function salesChannelAllowsGuestType(guestType, salesChannel = 'WEB') {
    if (!guestType.allowed_sales_channels) {
        return true;
    }

    return guestType.allowed_sales_channels.indexOf(salesChannel) >= 0;
}

export function filterTourPrice(tourPrice, isCommercialAccount) {
    const isCommercialTour =
        commercialAcadiaTourIds.indexOf(tourPrice.tour_id) > -1 ||
        commercialMountEvansTourIds.indexOf(tourPrice.tour_id) > -1 ||
        commercialRedRockCanyonTourIds.indexOf(tourPrice.tour_id) > -1;
    if (isCommercialAccount) {
        return isCommercialTour;
    }
    return !isCommercialTour;
}

export function getAgeRangeDescription({ min_age: minAge, max_age: maxAge }) {
    if (minAge) {
        if (maxAge) {
            return `ages ${minAge}-${maxAge}`;
        }

        return `ages ${minAge}+`;
    }
    if (maxAge) {
        return `ages ${maxAge} and under`;
    }

    return undefined;
}

/**
 * Evaluates if a tour has any external guest types:
 *    (1) uses facility guest types b/c it has the excluded sales channel property for each guest type id
 *    (2) filters the facility guest list down to only those allowed within the tour
 *    (3) filters again by the allowed_sales_channels property
 * @param facilityGuestTypes Array
 * @param tour Object
 * @returns {boolean}
 */
export const tourHasExternalGuestTypes = (facilityGuestTypes = [], tour = {}) => {
    return (
        facilityGuestTypes.filter((guestType) => {
            return (
                tour?.allowed_guest_type_ids?.includes(guestType?.guest_type_id) &&
                (!guestType?.allowed_sales_channels ||
                    guestType?.allowed_sales_channels?.indexOf('WEB') >= 0)
            );
        }).length > 0
    );
};

export const getGuestCounterOptions = ({ allowedGuestTypeIds, guestTypes }) => {
    const guestCounterOptions = [];

    const filteredGuestTypes = guestTypes.filter((guestType) =>
        salesChannelAllowsGuestType(guestType)
    );

    const guestTypeMap = keyBy(filteredGuestTypes, 'guest_type_id');

    if (!isEmpty(allowedGuestTypeIds)) {
        allowedGuestTypeIds.forEach((guestTypeId) => {
            if (guestTypeMap[guestTypeId]) {
                const { type } = guestTypeMap[guestTypeId];
                const description = getAgeRangeDescription(guestTypeMap[guestTypeId]);

                guestCounterOptions.push({
                    key: guestTypeId,
                    type,
                    description,
                });
            }
        });
    }

    return guestCounterOptions;
};

export const getMinTotalGuestsByGuestType = (
    purchaseMinimumsByGuestType,
    accountType = 'ANY'
) => {
    if (!purchaseMinimumsByGuestType) return null;

    const minTotalGuestsByGuestType = {};
    for (const guestType in purchaseMinimumsByGuestType) {
        if (purchaseMinimumsByGuestType.hasOwnProperty(guestType)) {
            minTotalGuestsByGuestType[guestType] =
                purchaseMinimumsByGuestType[guestType][accountType];
        }
    }
    return minTotalGuestsByGuestType;
};

export const getMaxTotalGuestsByGuestType = (
    purchaseMaximumsByGuestType,
    accountType = 'ANY'
) => {
    if (!purchaseMaximumsByGuestType) return null;

    const maxTotalGuestsByGuestType = {};
    for (const guestType in purchaseMaximumsByGuestType) {
        if (purchaseMaximumsByGuestType.hasOwnProperty(guestType)) {
            maxTotalGuestsByGuestType[guestType] =
                purchaseMaximumsByGuestType[guestType][accountType];
        }
    }
    return maxTotalGuestsByGuestType;
};

export const getCustomGuestTypeText = (facilityId) => {
    if (isSanGabriel(facilityId)) {
        return 'You must purchase a Primary Vehicle ticket in order to add Secondary Vehicle tickets.';
    }
    return '';
};
